import type { CommunityScheduledQueuePost } from './_components/scheduledQueue/ScheduledQueueRow';

export type CommunityScheduledPostAction = 'edit' | 'reschedule' | 'publish' | 'takeover' | 'delete';

export interface CommunityScheduledPostActionItem {
  action: CommunityScheduledPostAction;
  isDisabled: boolean;
}

const FAILURE_REASONS = [
  'channel_unavailable',
  'author_unavailable',
  'attachable_unavailable',
  'community_unavailable',
  'mentions_unavailable',
] as const;

export interface CommunityScheduledPostProblem {
  /** A failed post did not publish at its time; an unpublishable one, still waiting, won't. */
  kind: 'failed' | 'unpublishable';
  /** time_passed: a failed post that nothing stops any more, which needs only a new time. */
  reason: (typeof FAILURE_REASONS)[number] | 'unknown' | 'time_passed';
}

type ScheduledPostProblemFields = Pick<CommunityScheduledQueuePost, 'status' | 'predicted_failure_reason'>;

type ScheduledPostActionFields = ScheduledPostProblemFields & Pick<CommunityScheduledQueuePost, 'takeover_mode'>;

export const communityScheduledPostProblem = (
  post: ScheduledPostProblemFields,
): CommunityScheduledPostProblem | undefined => {
  const isFailed = post.status === 'failed';
  const failure = post.predicted_failure_reason;
  if (!failure) return isFailed ? { kind: 'failed', reason: 'time_passed' } : undefined;

  return {
    kind: isFailed ? 'failed' : 'unpublishable',
    reason: FAILURE_REASONS.find((reason) => reason === failure) ?? 'unknown',
  };
};

// A post that failed or won't publish is fixed by an edit, so it offers neither a reschedule nor a publish.
const shownActions = (
  post: ScheduledPostActionFields,
  { isAuthor, isAdmin }: { isAuthor: boolean; isAdmin: boolean },
): CommunityScheduledPostAction[] => {
  if (!isAuthor && !isAdmin) return ['delete'];

  const takeover: CommunityScheduledPostAction[] = isAdmin && !isAuthor && post.takeover_mode ? ['takeover'] : [];

  if (communityScheduledPostProblem(post)) return ['edit', ...takeover, 'delete'];
  return ['edit', 'reschedule', 'publish', ...takeover, 'delete'];
};

export const communityScheduledPostActions = (
  post: ScheduledPostActionFields,
  viewer: { isAuthor: boolean; isAdmin: boolean },
): CommunityScheduledPostActionItem[] =>
  shownActions(post, viewer).map((action) => ({
    action,
    isDisabled: action === 'edit' && post.takeover_mode === 'required',
  }));
